import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import {
  ApplePayDebugEntry,
  applePayDebug,
  clearApplePayDebug,
  formatApplePayDebugEntries,
  getApplePayDebugEntries,
  getApplePayEnvironment,
  installApplePayGlobalErrorCapture,
  isApplePayDebugEnabled,
  setApplePayDebugEnabled,
  subscribeApplePayDebug,
} from "utils/applePayDebug";

const levelColors: Record<ApplePayDebugEntry["level"], string> = {
  info: "#9ad0ff",
  warn: "#ffd166",
  error: "#ff7b7b",
};

const panelStyle: React.CSSProperties = {
  position: "fixed",
  right: 8,
  bottom: 8,
  width: "min(520px, calc(100vw - 16px))",
  maxHeight: "45vh",
  zIndex: 2147483000,
  display: "flex",
  flexDirection: "column",
  background: "rgba(15, 15, 20, 0.96)",
  color: "#f5f5f5",
  fontFamily:
    "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
  fontSize: 11,
  lineHeight: 1.4,
  borderRadius: 8,
  boxShadow: "0 6px 24px rgba(0,0,0,0.45)",
  overflow: "hidden",
};

const headerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  padding: "6px 8px",
  background: "#000",
  borderBottom: "1px solid #333",
};

const buttonStyle: React.CSSProperties = {
  background: "#2a2a33",
  color: "#fff",
  border: "1px solid #444",
  borderRadius: 4,
  padding: "2px 8px",
  fontSize: 11,
  cursor: "pointer",
};

/**
 * Mounted once at the app root. Renders nothing unless Apple Pay debugging is
 * enabled (open any page with `?apdebug=1`; `?apdebug=0` or Off turns it off).
 */
const ApplePayDebugPanel = () => {
  const enabled = useSyncExternalStore(
    subscribeApplePayDebug,
    isApplePayDebugEnabled,
    () => false
  );
  const entries = useSyncExternalStore(
    subscribeApplePayDebug,
    getApplePayDebugEntries,
    getApplePayDebugEntries
  );
  const [collapsed, setCollapsed] = useState(false);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">(
    "idle"
  );

  const handleCopy = useCallback(async () => {
    const text = formatApplePayDebugEntries(entries);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    }
    window.setTimeout(() => setCopyState("idle"), 1500);
  }, [entries]);

  useEffect(() => {
    if (!enabled) return undefined;
    applePayDebug("info", "debug panel enabled", {
      pageUrl: window.location.href,
      ...getApplePayEnvironment(),
    });
    return installApplePayGlobalErrorCapture();
  }, [enabled]);

  if (!enabled) return null;

  const errorCount = entries.filter((entry) => entry.level === "error").length;

  return (
    <div style={panelStyle} data-testid="apple-pay-debug-panel">
      <div style={headerStyle}>
        <strong style={{ flex: 1 }}>
          Apple Pay debug · {entries.length} entries
          {errorCount > 0 ? ` · ${errorCount} errors` : ""}
        </strong>
        <button
          type="button"
          style={buttonStyle}
          onClick={() => setCollapsed((value) => !value)}
        >
          {collapsed ? "Show" : "Hide"}
        </button>
        <button type="button" style={buttonStyle} onClick={handleCopy}>
          {copyState === "copied"
            ? "Copied"
            : copyState === "failed"
              ? "Copy failed"
              : "Copy"}
        </button>
        <button
          type="button"
          style={buttonStyle}
          onClick={() =>
            applePayDebug("info", "environment snapshot", {
              pageUrl: window.location.href,
              ...getApplePayEnvironment(),
            })
          }
          title="Log the current device and page details"
        >
          Env
        </button>
        <button type="button" style={buttonStyle} onClick={clearApplePayDebug}>
          Clear
        </button>
        <button
          type="button"
          style={buttonStyle}
          onClick={() => setApplePayDebugEnabled(false)}
          title="Turn the debug panel off (open a page with ?apdebug=1 to re-enable)"
        >
          Off
        </button>
      </div>
      {!collapsed && (
        <div
          style={{ overflow: "auto", padding: "6px 8px", userSelect: "text" }}
        >
          {entries.length === 0 ? (
            <div style={{ color: "#888" }}>Waiting for Apple Pay events…</div>
          ) : (
            entries.map((entry) => (
              <div
                key={entry.id}
                style={{
                  padding: "3px 0",
                  borderBottom: "1px solid #222",
                  wordBreak: "break-word",
                }}
              >
                <span style={{ color: "#888" }}>{entry.time}</span>{" "}
                <span
                  style={{ color: levelColors[entry.level], fontWeight: 600 }}
                >
                  {entry.level.toUpperCase()}
                </span>{" "}
                <span>{entry.message}</span>
                {entry.data && (
                  <pre
                    style={{
                      margin: "2px 0 0 0",
                      whiteSpace: "pre-wrap",
                      color: "#cfcfcf",
                    }}
                  >
                    {JSON.stringify(entry.data, null, 1)}
                  </pre>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default ApplePayDebugPanel;
